/**
 * Parses a CC4 file locally in the browser without making API calls
 * @param {File} file - The CC4 file to parse
 * @param {Function} onProgress - Optional callback for progress updates (0-100)
 * @returns {Promise<Object>} - The parsed CC4 data
 */
export const parseCC4FileLocally = async (file, onProgress = () => {}) => {
  return new Promise((resolve, reject) => {
    try {
      // Start progress indication
      onProgress(10);
      
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        try {
          onProgress(50);
          
          // Get the file content as text
          const textContent = event.target.result;
          
          // Parse the CC4 file format (text-based INI style)
          const parsedData = parseCC4TextFormat(textContent);
          
          onProgress(90);
          
          // Return the parsed data
          resolve({
            success: true,
            result: JSON.stringify(parsedData)
          });
          
          onProgress(100);
        } catch (error) {
          reject(new Error(`Error parsing CC4 file: ${error.message}`));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read the file'));
      };
      
      // Read the file as text instead of ArrayBuffer
      reader.readAsText(file);
      
    } catch (error) {
      reject(new Error(`Error processing CC4 file: ${error.message}`));
    }
  });
};

/**
 * Parses a CC4 file in text-based INI format
 * @param {string} textContent - The CC4 file content as text
 * @returns {Object} - Extracted data from the CC4 file
 */
function parseCC4TextFormat(textContent) {
  // Split the content into lines
  const lines = textContent.split(/\r?\n/);
  
  // Initialize variables to store parsed data
  const sections = {};
  let currentSection = null;
  
  // Parse each line
  for (const line of lines) {
    // Skip empty lines
    if (!line.trim()) continue;
    
    // Check if this is a section header [SECTION_NAME]
    const sectionMatch = line.match(/^\[(.*?)\]$/);
    if (sectionMatch) {
      currentSection = sectionMatch[1];
      sections[currentSection] = {};
      continue;
    }
    
    // If we're in a section, parse key-value pairs
    if (currentSection) {
      const keyValueMatch = line.match(/^([^=]+)=(.*)$/);
      if (keyValueMatch) {
        const key = keyValueMatch[1].trim();
        const value = keyValueMatch[2].trim();
        sections[currentSection][key] = value;
      }
    }
  }
  
  // Extract relevant data from parsed sections
  const patientInfo = extractPatientInfo(sections);
  const vo2Data = extractVO2Data(sections);
  
  // Extract stage data
  const { dataSamples, stageData } = extractDataSamples(sections);
  vo2Data.dataSamples = dataSamples;
  vo2Data.stageData = stageData;
  
  // Return the extracted data
  return {
    ...vo2Data,
    patientInfo,
    fileMetadata: {
      format: 'CC4-TEXT',
      version: sections.VERSION?.RECORD_VERSION || '4'
    }
  };
}

/**
 * Extracts patient information from parsed CC4 sections
 * @param {Object} sections - The parsed sections from the CC4 file
 * @returns {Object} - Patient information
 */
function extractPatientInfo(sections) {
  const patientInfo = {};
  
  // Extract data from PATIENT_INFO section
  if (sections.PATIENT_INFO) {
    const info = sections.PATIENT_INFO;
    patientInfo.age = info.Age || '';
    patientInfo.gender = info.Gender === '1' ? 'Male' : 'Female';
    patientInfo.height = info.Height_Cm || '';
    patientInfo.weight = info.Weight_Kg || '';
    patientInfo.bmi = info.BMI || '';
  }
  
  // Extract name data from NAMES section
  if (sections.NAMES) {
    const names = sections.NAMES;
    patientInfo.firstName = names.Patient_First || '';
    patientInfo.lastName = names.Patient_Last || '';
    patientInfo.fullName = `${names.Patient_First || ''} ${names.Patient_Last || ''}`.trim();
  }
  
  // Extract device info
  if (sections.DEVICE_INFO) {
    patientInfo.deviceInfo = {
      serialNumber: sections.DEVICE_INFO.Serial_Num || '',
      modelNumber: sections.DEVICE_INFO.Model_Num || '',
      dateTime: sections.DEVICE_INFO.DateTime || ''
    };
  }
  
  return patientInfo;
}

/**
 * Extracts VO2 Max and related data from parsed CC4 sections
 * @param {Object} sections - The parsed sections from the CC4 file
 * @returns {Object} - VO2 Max and related data
 */
function extractVO2Data(sections) {
  const result = {
    vo2Max: '',
    vo2MaxUnit: 'ml/kg/min',
    heartRateMax: '',
    heartRateRest: '',
    rmrValue: '',
    rmrUnit: 'kcal/day',
    anaerobicThreshold: '',
    anaerobicThresholdPercent: '',
    aerobicThreshold: '',
    aerobicThresholdPercent: '',
    predictedVO2Max: '',
    predictedMaxHR: '',
    recoveryHeartRates: {},
    recoveryVO2Values: {},
    recoveryIndices: {},
    peakHeartRate: '',
    fitnessLevel: '',
    testDate: '',
    testId: '',
    reportId: ''
  };
  
  // Extract data from VO2_TEST_RESULTS section
  if (sections.VO2_TEST_RESULTS) {
    const vo2Data = sections.VO2_TEST_RESULTS;
    
    // Extract VO2 Max value
    if (vo2Data.MAX_VO2) {
      result.vo2Max = parseFloat(vo2Data.MAX_VO2).toFixed(2);
    }
    
    // Extract heart rate data
    if (vo2Data.MAX_HeartRate) {
      result.heartRateMax = vo2Data.MAX_HeartRate;
    }
    
    if (vo2Data.MIN_HeartRate) {
      result.heartRateRest = vo2Data.MIN_HeartRate;
    }
    
    // Extract RMR (calories) if available
    if (vo2Data.MAX_Calories) {
      result.rmrValue = Math.round(parseFloat(vo2Data.MAX_Calories));
    }
    
    // Extract anaerobic threshold data
    if (vo2Data.AT_VO2) {
      result.anaerobicThreshold = parseFloat(vo2Data.AT_VO2).toFixed(2);
    }
    
    if (vo2Data.AT_HeartRate) {
      result.anaerobicThresholdHR = vo2Data.AT_HeartRate;
    }
    
    if (vo2Data.VT_VO2) {
      result.aerobicThreshold = parseFloat(vo2Data.VT_VO2).toFixed(2);
    }
    
    if (vo2Data.VT_HeartRate) {
      result.aerobicThresholdHR = vo2Data.VT_HeartRate;
    }
    

    if (vo2Data.AT_Percent) {
      result.anaerobicThresholdPercent = vo2Data.AT_Percent;
    }
    
    // Extract predicted values
    if (vo2Data.PRED_VO2) {
      result.predictedVO2Max = parseFloat(vo2Data.PRED_VO2).toFixed(2);
    }
    
    if (vo2Data.PRED_MaxHR) {
      result.predictedMaxHR = vo2Data.PRED_MaxHR;
    }
    
    // Extract peak heart rate
    if (vo2Data.Peak_HeartRate) {
      result.peakHeartRate = vo2Data.Peak_HeartRate;
    }
    
    // Extract fitness level
    if (vo2Data.FitnessLevel) {
      result.fitnessLevel = vo2Data.FitnessLevel;
    }
    
    
    // Extract recovery heart rates, VO2 values, and indices
    for (let i = 1; i <= 5; i++) {
      // Extract recovery heart rates
      if (vo2Data[`Rec_${i}_HR`]) {
        result.recoveryHeartRates[`rec${i}`] = vo2Data[`Rec_${i}_HR`];
      } else if (vo2Data[`REC_HR_${i}`]) { // Fallback to alternative format
        result.recoveryHeartRates[`rec${i}`] = vo2Data[`REC_HR_${i}`];
      }
      
      // Extract recovery VO2 values
      if (vo2Data[`Rec_${i}_VO2`]) {
        result.recoveryVO2Values[`rec${i}`] = vo2Data[`Rec_${i}_VO2`];
      }
      
      // Extract recovery indices
      if (vo2Data[`Rec_${i}_Idx`]) {
        result.recoveryIndices[`rec${i}`] = vo2Data[`Rec_${i}_Idx`];
      }
    }
  }
  
  // Extract test information
  if (sections.TEST_INFO) {
    const testInfo = sections.TEST_INFO;
    if (testInfo.Test_Date) {
      result.testDate = testInfo.Test_Date;
    }
    if (testInfo.Test_ID) {
      result.testId = testInfo.Test_ID;
    }
    if (testInfo.Report_ID) {
      result.reportId = testInfo.Report_ID;
    }
  }
  
  // Extract heart rate zones
  result.heartRateZones = extractHeartRateZones(sections);
  
  // Data samples are extracted separately
  
  return result;
}

/**
 * Extracts heart rate zones data from parsed CC4 sections
 * @param {Object} sections - The parsed sections from the CC4 file
 * @returns {Object} - Heart rate zones data
 */
function extractHeartRateZones(sections) {
  const heartRateZones = {};
  
  // Extract data from Heart_Rate_Zones_Table section
  if (sections.Heart_Rate_Zones_Table) {
    const zonesData = sections.Heart_Rate_Zones_Table;
    
    // Extract each zone (typically 5 zones)
    for (let i = 1; i <= 5; i++) {
      const zoneKey = `Zone_${i}`;
      if (zonesData[`${zoneKey}_Low`] && zonesData[`${zoneKey}_High`]) {
        heartRateZones[`zone${i}`] = {
          low: zonesData[`${zoneKey}_Low`],
          high: zonesData[`${zoneKey}_High`],
          name: zonesData[`${zoneKey}_Name`] || `Zone ${i}`,
          description: zonesData[`${zoneKey}_Description`] || ''
        };
      }
    }
    
    // Extract HR_Zone values (0-4 format)
    for (let i = 0; i <= 4; i++) {
      if (zonesData[`HR_Zone_Low_${i}`] && zonesData[`HR_Zone_High_${i}`]) {
        heartRateZones[`hrZone${i}`] = {
          low: zonesData[`HR_Zone_Low_${i}`],
          high: zonesData[`HR_Zone_High_${i}`]
        };
      }
    }
    
    // Extract training zones if available
    if (zonesData.Training_Zone_1 && zonesData.Training_Zone_2) {
      heartRateZones.trainingZones = {
        zone1: zonesData.Training_Zone_1,
        zone2: zonesData.Training_Zone_2
      };
    }
  }
  
  return heartRateZones;
}

/**
 * Extracts data samples for the VO2 max graph from parsed CC4 sections
 * @param {Object} sections - The parsed sections from the CC4 file
 * @returns {Array} - Array of data samples for the graph
 */
function extractDataSamples(sections) {
  // const dataSamples = [];
  // const stageData = {};
  
  // // Extract data from DATA_SAMPLES section
  // if (sections.DATA_SAMPLES) {
  //   const samplesData = sections.DATA_SAMPLES;
    
  //   // Determine the number of samples
  //   const sampleKeys = Object.keys(samplesData).filter(key => key.startsWith('Sample_'));
  //   const sampleIndices = sampleKeys.map(key => {
  //     const match = key.match(/Sample_(\d+)_/);
  //     return match ? parseInt(match[1]) : -1;
  //   }).filter(index => index !== -1);
    
  //   // Get unique sample indices
  //   const uniqueIndices = [...new Set(sampleIndices)].sort((a, b) => a - b);
    
  //   // Extract each sample
  //   for (const index of uniqueIndices) {
  //     const sample = {};
      
  //     // Extract common fields
  //     sample.time = samplesData[`Sample_${index}_Time`] || '';
  //     sample.hr = samplesData[`Sample_${index}_HR`] || '';
  //     sample.vo2 = samplesData[`Sample_${index}_VO2`] || '';
  //     sample.vco2 = samplesData[`Sample_${index}_VCO2`] || '';
  //     sample.rer = samplesData[`Sample_${index}_RER`] || '';
  //     sample.ve = samplesData[`Sample_${index}_VE`] || '';
  //     sample.calories = samplesData[`Sample_${index}_Calories`] || '';
  //     sample.cadence = samplesData[`Sample_${index}_Cadence`] || '';
  //     sample.stage = samplesData[`Sample_${index}_Stage`] || '';
  //     sample.index = index;
      
  //     // Add to samples array if we have at least time and HR
  //     if (sample.time && sample.hr) {
  //       dataSamples.push(sample);
  //     }
  //   }
  // }
  
  // // Extract stage data for dynamic table
  // if (sections.STAGES) {
  //   const stagesData = sections.STAGES;
    
  //   // Extract each stage (0-74)
  //   for (let i = 0; i <= 74; i++) {
  //     const stagePrefix = `Stage_${i}`;
  //     const stage = {};
      
  //     // Check if this stage exists
  //     const hasStage = Object.keys(stagesData).some(key => key.startsWith(stagePrefix));
      
  //     if (hasStage) {
  //       // Extract stage data
  //       stage.grade = stagesData[`${stagePrefix}_Grade`] || '';
  //       stage.speed = stagesData[`${stagePrefix}_Speed`] || '';
  //       stage.time = stagesData[`${stagePrefix}_Time`] || '';
  //       stage.duration = stagesData[`${stagePrefix}_Duration`] || '';
  //       stage.distance = stagesData[`${stagePrefix}_Distance`] || '';
  //       stage.calories = stagesData[`${stagePrefix}_Calories`] || '';
        
  //       stageData[i] = stage;
  //     }
  //   }
  // }
  
  // return { dataSamples, stageData };

  const dataSamples = [];
  const stageData = {};
  
  // Extract sample data if available
  if (sections.DATA_SAMPLES) {
    const data = sections.DATA_SAMPLES;
    const sampleCount = parseInt(data.Samples || '0', 10);
    
    // Extract each sample
    for (let i = 0; i < sampleCount; i++) {
      const sample = {
        index: i,
        time: parseFloat(data[`TestTime_${i}`] || '0'),
        vo2: parseFloat(data[`VO2_${i}`] || '0'),
        vco2: parseFloat(data[`VCO2_${i}`] || '0'),
        ve: parseFloat(data[`Ve_${i}`] || '0'),
        hr: parseFloat(data[`HR_${i}`] || '0'),
        rr: parseFloat(data[`RR_${i}`] || '0'),
        feo2: parseFloat(data[`FeO2_${i}`] || '0'),
        feco2: parseFloat(data[`FeCO2_${i}`] || '0'),
        vevo2: parseFloat(data[`VeVO2_${i}`] || '0'),
        vevco2: parseFloat(data[`VeVCO2_${i}`] || '0'),
        vol: parseFloat(data[`Vol_${i}`] || '0'),
        tv: parseFloat(data[`TV_${i}`] || '0'),
        stage: parseInt(data[`Stage_${i}`] || '-1', 10),
        speed: parseFloat(data[`Speed_${i}`] || '0'),
        grade: parseFloat(data[`Grade_${i}`] || '0'),
        watts: parseFloat(data[`Watts_${i}`] || '0'),
        mode: parseInt(data[`Mode_${i}`] || '0', 10),
        state: parseInt(data[`State_${i}`] || '0', 10),
        error: parseInt(data[`Error_${i}`] || '0', 10)
      };
      
      dataSamples.push(sample);
      
      // Track stage data
      if (sample.stage >= 0) {
        const stageKey = `stage_${sample.stage}`;
        if (!stageData[stageKey]) {
          stageData[stageKey] = {
            stage: sample.stage,
            startTime: sample.time,
            endTime: sample.time,
            samples: [],
            avgValues: {}
          };
        } else {
          stageData[stageKey].endTime = sample.time;
        }
        stageData[stageKey].samples.push(sample);
      }
    }
  }
  
  return { dataSamples, stageData };

}

/**
 * Fallback parser for when the text parsing fails
 * This attempts to extract data from the file name or other metadata
 * @param {File} file - The original file object
 * @returns {Object} - Extracted data or empty values
 */
export const extractFromFileMetadata = (file) => {
  const filename = file.name.toLowerCase();
  const result = {
    vo2Max: '',
    heartRateMax: '',
    heartRateRest: '',
    rmrValue: '',
    heartRateZones: {},
    dataSamples: [],
    recoveryHeartRates: {}
  };
  
  // Try to extract VO2 Max from filename (e.g., "patient_vo2max_42.3_cc4.cc4")
  const vo2Match = filename.match(/vo2max[_-]?(\d+\.?\d*)/);
  if (vo2Match && vo2Match[1]) {
    result.vo2Max = vo2Match[1];
  }
  
  // Try to extract RMR from filename (e.g., "patient_rmr_1800_cc4.cc4")
  const rmrMatch = filename.match(/rmr[_-]?(\d+)/);
  if (rmrMatch && rmrMatch[1]) {
    result.rmrValue = rmrMatch[1];
  }
  
  return result;
};
